import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Leader-election for Supabase auto-refresh.
 *
 * Across all tabs of the same browser/origin, exactly ONE tab holds an
 * exclusive Web Lock and runs `supabase.auth.startAutoRefresh()`. All
 * other tabs sit idle in `navigator.locks.request()` and let the leader
 * refresh the token. Once the leader writes the rotated session to
 * localStorage, follower tabs pick it up via the storage event (handled
 * in AuthContext).
 *
 * This is the documented fix for the GoTrue 429 refresh-token storms.
 */

const LOCK_NAME = 'sb-auth-refresh-leader';

let initialized = false;

type LockManager = {
  request: <R>(
    name: string,
    options: { mode: 'exclusive' },
    callback: () => Promise<R>
  ) => Promise<R>;
};

function getLockManager(): LockManager | undefined {
  if (typeof navigator === 'undefined') return undefined;
  return (navigator as Navigator & { locks?: LockManager }).locks;
}

export function initAuthLeader(supabase: SupabaseClient): void {
  if (initialized) return;
  initialized = true;
  if (typeof window === 'undefined') return;

  const locks = getLockManager();

  // Fallback: no Web Locks API. Run a slow manual refresh in this tab.
  if (!locks?.request) {
    const FALLBACK_INTERVAL_MS = 50 * 60 * 1000; // 50 minutes
    const tick = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (data.session) await supabase.auth.refreshSession();
      } catch (e) {
        console.warn('Auth leader fallback refresh failed:', e);
      }
    };
    setInterval(tick, FALLBACK_INTERVAL_MS);
    return;
  }

  let isLeader = false;

  const becomeLeader = async () => {
    isLeader = true;
    try {
      await supabase.auth.startAutoRefresh();
    } catch (e) {
      console.warn('startAutoRefresh failed:', e);
    }
    // Hold the lock until the tab is closed/hidden. We resolve the
    // promise via an external trigger so other tabs can take over.
    await new Promise<void>((resolve) => {
      const release = () => {
        isLeader = false;
        try {
          supabase.auth.stopAutoRefresh();
        } catch {}
        window.removeEventListener('pagehide', release);
        document.removeEventListener('visibilitychange', onVis);
        resolve();
      };
      const onVis = () => {
        if (document.visibilityState === 'hidden') release();
      };
      window.addEventListener('pagehide', release, { once: true });
      document.addEventListener('visibilitychange', onVis);
    });
  };

  const tryAcquire = () => {
    locks
      .request(LOCK_NAME, { mode: 'exclusive' }, becomeLeader)
      .catch((e) => console.warn('Auth leader lock error:', e))
      .finally(() => {
        // Re-attempt acquisition when tab becomes visible again.
        if (document.visibilityState === 'visible' && !isLeader) {
          setTimeout(tryAcquire, 250);
        }
      });
  };

  // Re-acquire on visibility regain (in case we released while hidden).
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && !isLeader) {
      tryAcquire();
    }
  });

  tryAcquire();
}
